import { useStore } from '@nanostores/react';
import { AccountFeatureFlag, accountHasFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import type { AugmentedAccount } from '@/services/auth/types';
import {
  hasSignupPublicFlag,
  SignupPublicFeatureFlag,
  signupPublicFlagsStore,
  type SignupPublicFlags,
} from '@/services/signup/public-flags-store';

type RouteOwner = 'hydra' | 'embedded';

/** The slice of the account that route ownership depends on. */
export type RouteOwnershipAccount = Pick<AugmentedAccount, 'feature_flags'> | null;

type RouteOwnershipPattern = {
  owner: RouteOwner;
  pattern: RegExp;
  /**
   * When set, the entry only applies while the account feature flag is on — a rollout
   * kill-switch: flag off, and the URL falls through to the default `embedded` owner. Before
   * the account loads, the flag reads its default (off ⇒ embedded), so any consumer that
   * evaluates ownership during render and caches the answer MUST re-evaluate once the
   * account arrives — use `useRouteOwner`/`useIsHydraRoute` for that.
   */
  flag?: AccountFeatureFlag;
  /**
   * Like `flag`, but gated on a *public* feature flag — the rollout mechanism
   * for pre-auth routes, where no account exists to carry an account flag.
   * While the public-flags fetch hasn't settled (a `null` store), gated
   * entries don't apply (⇒ embedded), mirroring the account-flag cold-load
   * behavior above. Render-time consumers are covered by the route gate's
   * loader; call-time consumers (the `window.__hydra__` bridge, navigation,
   * active-route-sync) instead rely on the eager pre-auth prefetch in
   * `utils/inject-globals.ts`, which resolves the store before any embedded app
   * can ask about `/signup`. Signup-specific on purpose: rollout bucketing uses
   * the signup-prefixed anonymous id, so this gate must not be casually reused
   * for non-signup routes.
   */
  signupPublicFlag?: SignupPublicFeatureFlag;
};

/**
 * Canonical route ownership map for URLs that cross the Hydra shell /
 * embedded-app boundary.
 *
 * TanStack owns exact route matching, but embedded apps still ask Hydra whether
 * a browser URL should stay in the shell. Keep those ownership decisions here
 * so active-route sync, global navigation helpers, and Ember interop do not
 * each grow their own handwritten route model.
 *
 * Entries with a `flag` are gated on an account feature flag (via `hasFeatureFlag`,
 * this module's only runtime data dependency) — every consumer, including the
 * `window.__hydra__.isHydraRoute` bridge Ember calls, reads the answer fresh per
 * navigation, so a flag flip propagates without any consumer changes.
 */
const ROUTE_OWNERSHIP_PATTERNS: Array<RouteOwnershipPattern> = [
  // Auth-entry routes migrated to Hydra (FPS-2341): login + its sub-steps,
  // logout, academy-login, and the legacy /users/login redirect.
  { owner: 'hydra', pattern: /^\/login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/logout(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/academy-login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/users\/login(?:[#/?]|$)/i },
  // The Journeys paywall, with no rollout flag and no embedded fallback. The embedded app still sends
  // expired accounts to this URL, so ownership must stay claimed here.
  { owner: 'hydra', pattern: /^\/trial-expired\/journeys(?:[#/?]|$)/i },
  // Signup funnel (FPS-2851): the whole /signup tree plus its legacy
  // /users/register and /users/signup aliases, dark behind the
  // `signup-migration` rollout flag. The funnel is pre-auth, so the gate is a
  // public feature flag (resolvable for anonymous visitors); flag off, the
  // URLs fall back to the embedded app, whose signup routes are intact.
  // `SignupRouteGate` renders the embedded host on the same flag so the
  // ownership answer and the rendered app always agree.
  {
    owner: 'hydra',
    pattern: /^\/signup(?:[#/?]|$)/i,
    signupPublicFlag: SignupPublicFeatureFlag.SignupMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/users\/(register|signup)(?:[#/?]|$)/i,
    signupPublicFlag: SignupPublicFeatureFlag.SignupMigration,
  },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/auto_archive/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/edit/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/confirm_delete/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/merge_options/i },
  // LLM configuration (FPS-2355): the whole route set — index, /new, and
  // /:id/edit — is Hydra-owned.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/llm_configuration/i },
  // Object-type settings (FPS-2357), dark behind the `hydra-objects-settings`
  // rollout flag: flag off, the URLs fall back to the embedded Ember app (whose
  // objects routes are intact). `WorkspaceObjectsSettingsGate` renders the Ember
  // host on the same flag so the ownership answer and the rendered app always
  // agree. The whole route set — index, `/new`, and `/edit/:id` — is claimed as
  // one unit and flips atomically on the flag.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/objects(?:\/|$|[#?])/i,
    flag: AccountFeatureFlag.HydraObjectsSettings,
  },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/language/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/anonymous_events/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/suppressions/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/sms-providers/i },
  // Email settings (FPS-2349), dark behind the `hydra-email-settings` rollout flag: flag off, the
  // URLs fall back to the embedded Ember app. `WorkspaceEmailSettingsGate` renders the Ember host on
  // the same flag so the ownership answer and the rendered app always agree.
  //
  // The tabs are enumerated rather than claiming the whole `/email` subtree because
  // `/email/account_verification` is served by the embedded app and is not part of this migration —
  // it must keep falling through to the `settings/$` splat.
  //
  // Every alternative ends terminally (optional trailing slash, then query, hash, or end of path),
  // so the claim covers exactly the URLs the route tree serves: the email root, each tab, and one
  // sending domain by id. A deeper path such as `/smtp/anything` or `/sending_domains/42/anything`
  // has no route behind it, so it must fall through rather than be claimed and 404 in Hydra.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/settings\/actions\/email(?:\/(?:sending_domains(?:\/[^/#?]+)?|dynamic_addresses|from_addresses|deliverability|smtp|defaults|suppression_list))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.HydraEmailSettings,
  },
  // Webhook configuration (FPS-2354): the whole subtree — index list, `/new`,
  // and `/:id/edit`.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/webhook_configuration(?:\/|$|[#?])/i,
  },
  // Account settings landing page (FPS-2287), unflagged. Matches ONLY the bare `/settings` path
  // (with an optional trailing slash, query, or hash). Unknown `/settings/*` subpaths must keep
  // falling through to the `settings/$` splat and render the embedded Ember app, so this is
  // deliberately not the whole subtree. Rollback = revert this entry (and the index route swap).
  { owner: 'hydra', pattern: /^\/settings\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/ai(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/slack-agent(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/account(?:[#/?]|$)/i },
  // Partner used to live under /settings/account/partner (covered by the
  // /settings/account pattern above); now it's a top-level settings path.
  { owner: 'hydra', pattern: /^\/settings\/partner(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/personal\/google-identities(?:[#/?]|$)/i },
  // Personal settings index (FPS-2861), dark behind the `personal_settings_in_hydra` rollout
  // flag: flag off, the URL falls back to the embedded Ember app. The index route renders the
  // embedded host on the same flag so ownership and the rendered app agree. Matches ONLY the
  // index path — `/settings/personal/security` (2FA management) is still Ember-owned, and
  // `/settings/personal/google-identities` has its own unflagged entry above.
  {
    owner: 'hydra',
    pattern: /^\/settings\/personal\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.PersonalSettingsInHydra,
  },
  { owner: 'hydra', pattern: /^\/settings\/roles(?:[#/?]|$)/i },
  // Team members: the index (list), `/add` (invite), `/members/:id/edit`
  // (FPS-2537), `/invite-guest` (FPS-2557), and `/join-requests/:id` (the approve
  // page) are Hydra-owned. `/set_password` is still Ember, so this matches only
  // those five shapes and leaves it to hand off to Ember. Widen to the full
  // subtree once set_password migrates.
  {
    owner: 'hydra',
    pattern: /^\/settings\/team(?:\/add|\/invite-guest|\/members\/[^/]+\/edit|\/join-requests\/[^/]+)?\/?(?:[#?]|$)/i,
  },
  // The workspaces list and create (/new) are both Hydra-owned now.
  { owner: 'hydra', pattern: /^\/settings\/workspaces(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/privacy/i },
  { owner: 'hydra', pattern: /^\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/settings\/experimental-features/i },
  // Security settings (FPS-2223), fully rolled out at the FPS-2767 cutover: no flag gate,
  // and the Ember pods are deleted. Matches ONLY the two real routes — the index and the
  // nested `/sso` page (each with an optional trailing slash, query, or hash). Deliberately
  // not the whole subtree: there is no layout or splat route under `/settings/security`, so
  // an unknown nested URL resolves to the `settings/$` splat and renders the embedded app.
  // Claiming it here would make ownership say `hydra` while Ember is on screen — and after
  // the teardown Ember cannot route it either. Widen only alongside a real Hydra route.
  { owner: 'hydra', pattern: /^\/settings\/security(?:\/sso)?\/?(?:[#?]|$)/i },
  // Plans & Billing: the whole `/settings/billing` subtree — shell plus every tab — is Hydra-owned
  // unconditionally. The subtree is owned as one unit, never split per-tab, so one pattern covers
  // it.
  { owner: 'hydra', pattern: /^\/settings\/billing(?:\/|$|[#?])/i },
  // `/settings/receipts` redirects into billing history. Match only the bare path —
  // `/settings/receipts/:id` is a server-rendered Stripe receipt served outside this app, so it
  // must not be captured here.
  { owner: 'hydra', pattern: /^\/settings\/receipts(?:$|[#?])/i },
  // `/settings/subscription` (FPS-2286) redirects into the Your Plan tab, owned here on the same
  // footing as the billing subtree above. Matches the bare path plus an optional trailing slash,
  // query, or hash — like `/settings` above, and unlike `/settings/receipts`, which must exclude
  // the trailing slash to protect its `:id` sub-path. There is no route beneath this one, so a
  // nested URL resolves to the `settings/$` splat and renders the embedded app.
  { owner: 'hydra', pattern: /^\/settings\/subscription\/?(?:[#?]|$)/i },
  // The top-level, chrome-less promo checkout page. Outside the billing tab area, but owned here on
  // the same footing as the billing subtree above.
  { owner: 'hydra', pattern: /^\/checkout(?:\/|$|[#?])/i },
  // Essentials onboarding (SELF-138): the chrome-less /onboarding pages. Net-new
  // Hydra routes with no embedded equivalent, so the entry is UNGATED on purpose —
  // gating ownership on `essentials_onboarding` would hand flag-off traffic to
  // the embedded app, which can only boot into a 404. Flag-off users get
  // Hydra's NotFoundPage from the route layout's FeatureFlagGuard instead.
  // Verified against customerio/ui's router.js: no URL space under /onboarding
  // exists there — the only "onboarding" route names are signup children
  // (/signup/onboarding-usage), which this pattern does not touch. CDP and
  // Design Studio bundles never receive
  // top-level URLs (getAppBundleFromUrl routes those by their own prefixes).
  { owner: 'hydra', pattern: /^\/onboarding(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/har-sanitizer/i },
  // Guest-invitation accept page: Hydra-owned. Reached from an email link; the Ember route is
  // retained only as a revert target until the teardown.
  { owner: 'hydra', pattern: /^\/accept-guest-invitation(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/no_accessible_workspaces/i },
  { owner: 'hydra', pattern: /^\/post_cancel/i },
  // The two legacy cancel URLs stay with the embedded app. Its routes redirect to this URL.
  { owner: 'hydra', pattern: /^\/account_canceled/i },
  { owner: 'hydra', pattern: /^\/confirm_account_notice/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/agent/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/tasks/i },
  // Boards (dashboard editor PoC): net-new Hydra-only routes with no embedded equivalent,
  // so the entry is UNGATED on purpose (like /onboarding above) — flag-off traffic must get
  // Hydra's NotFoundPage from the route's FeatureFlagGuard, not the embedded app.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/boards/i },
  // Both taxonomy URL shapes: `analysis` pre-rename, `reports` once the flag is on. The
  // non-canonical one redirects to the other, but Hydra owns and serves both.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(analysis|reports)\/deliverability/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(analysis|reports)\/email-clients/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/health/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/home/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/pipelines/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/geosets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?exports/i },
  // Collections (SPE-3918), fully rolled out at the SPE-3923 cutover: `collections_in_hydra` hit
  // 100% on 2026-08-13 (FlightDeck `fully_rolled_out_at`), before the gate came off, so no account
  // changes app on merge; the Ember collections pods are deleted in ui#16131. The pattern still
  // claims only the ported subpaths (`data`/`importing`/`usage`); anything deeper falls through to
  // `embedded`.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/collections(?:\/[^/?#]+(?:\/(?:data|importing|usage))?)?\/?(?:[#?]|$)/i,
  },
  // Activity logs (FPS-2304). Both taxonomy segments are Hydra-owned; the future
  // `profile_activity` segment redirects to the canonical `activity_logs` page.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/activity_logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profile_activity/i },
  // Outbox tabs + landing paths (FPS-2305 migration, FPS-2595 cutover), across both taxonomy
  // segments (`outbox` current, `message_activity` future). Fully rolled out at the FPS-3422 flag
  // removal: `outbox-migration` hit 100% on 2026-08-12 (FlightDeck `fully_rolled_out_at`, progressive
  // rollout and new signups both 100%, no per-account overrides), before the gate came off, so no
  // account changes app on merge; the Ember outbox pods are frozen in ui#16072 and deleted in
  // ui#16225. Owned: the Deliveries/Sent tab + its `/:deliveryId` detail, the Drafts tab **list**,
  // and the index + `/welcome` landing paths (`OutboxRouteView` redirects those to the default
  // tab). Both tab-name spellings (`deliveries`/`sent`) are claimed in each segment so a
  // cross-rename URL normalizes in React instead of booting Ember. The draft detail/edit
  // (`/drafts/:id`…) and any deeper path aren't migrated, so they fall through to `embedded`.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/outbox(?:\/(?:(?:deliveries|sent)(?:\/[^/]+)?|drafts|welcome))?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/message_activity(?:\/(?:(?:deliveries|sent)(?:\/[^/]+)?|drafts|welcome))?\/?(?:[#?]|$)/i,
  },
  // Redirect-only Journeys URLs, forwarded natively (FPS-2346) — see
  // `routes/-journeys-redirects.tsx`. Unflagged: each is a pure forward with no page of its
  // own, so there is nothing for a rollout flag to dark-launch. The Ember pods stay frozen
  // in place as the revert target until the teardown.
  //
  // Each pattern matches only the URL shapes Ember registered, so anything deeper keeps
  // falling through to the embedded app exactly as it does today.
  //
  // The pre-Outbox top-level `/drafts` and `/deliveries`, which forward to the Outbox tabs.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/drafts\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/deliveries\/?(?:[#?]|$)/i },
  // The pre-v2 `triggered` name for campaigns: the list and the create page, nothing else.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/triggered(?:\/new)?\/?(?:[#?]|$)/i },
  // `v2/*` URLs from before the v2 app dropped its path prefix.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/v2(?:\/|$|[#?])/i },
  // `/setup-guide` and everything under it, which forward to the dashboard. This is the
  // bookmark/marketing-link stub only — the real setup guide lives in workspace settings and
  // is not touched by this entry.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/setup-guide(?:\/|$|[#?])/i },
  // The entire layouts feature is Hydra-owned now (FPS-2585): list
  // (FPS-2299), starters (FPS-2556), detail (FPS-2571), and create/edit
  // (FPS-2555). Claim the whole `/journeys/layouts` subtree.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/layouts(?:\/|$|[#?])/i },
  // Standalone template dispatcher (FPS-2300). The trailing guard keeps the
  // pattern from claiming Ember's `/journeys/composer/.../templates/...` URLs.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?template(?:[#/?]|$)/i },
  // Integrations directory (FPS-2314), unconditional since the `hydra-integrations-directory`
  // rollout flag reached 100% and was removed — the Ember directory pods are gone, so Hydra is
  // the only thing serving these URLs. TWO entries, because the migration drops the `journeys`
  // segment from the URL:
  //
  //   1. `/integrations/...` — where the migrated page actually lives.
  //   2. `/journeys/integrations/...` — the old Ember URL, kept claimed purely so
  //      `JourneysIntegrationsHandoff` can forward it to (1). Nothing in Hydra links here any
  //      more; it exists for bookmarks and any stale link still pointing at the Ember spelling.
  //
  // Both match ONLY the six real routes — the `/integrations` and `/integrations/all` indexes
  // (each redirecting to the Connections tab), the `/all/overview` and `/all/directory` tabs,
  // and the URL-only `/all/developers` + `/all/webhooks` sub-tabs (FPS-2655; reachable by
  // direct URL, absent from the tab nav) — each with an optional trailing slash, query, or
  // hash. Deliberately narrow: the nested connector routes (`/integrations/:type`,
  // `/sql-sync/*`, `warehouse`, `audiences`, `reporting-webhooks`, …) have no Hydra route,
  // so they must keep falling through to the embedded app. Widen only alongside a real
  // Hydra route.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/integrations(?:\/all(?:\/(?:overview|directory|developers|webhooks))?)?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/integrations(?:\/all(?:\/(?:overview|directory|developers|webhooks))?)?\/?(?:[#?]|$)/i,
  },
  // Reporting webhooks (FPS-2648 + FPS-2659) — fully migrated, so both entries are unconditional.
  // The Ember route set was deleted in ui#16224. TWO entries, because the migration drops the
  // `journeys` segment from the URL:
  //
  //   1. `/integrations/reporting-webhooks/...` — where the migrated pages live.
  //   2. `/journeys/integrations/reporting-webhooks/...` — the old Ember URLs. Hydra claims them
  //      only so `JourneysReportingWebhooksHandoff` can forward to (1), which keeps existing links
  //      and bookmarks working now that Ember no longer serves them at all.
  //
  // Both match the WHOLE subtree — the list, `/new`, and the bare-id edit page.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/reporting-webhooks(?:\/|$|[#?])/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/reporting-webhooks(?:\/|$|[#?])/i,
  },
  // Native data-out connectors — `/integrations/data_out/:type` (mixpanel, amplitude) — dark behind
  // `data-out-integrations-migration` (FPS-2800). Same two-entry shape and the same reasoning as
  // reporting-webhooks above: the migrated URL drops `journeys`, and the `journeys` twin is claimed
  // only so `JourneysDataOutHandoff` can forward to it (rendering the embedded host while the flag
  // is off), which keeps every Ember-side directory link working untouched.
  //
  // A connector type is required: `/data_out` with no `:type` is not a page in either app, so the
  // pattern demands a non-empty segment and leaves the bare path with the embedded app.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/data_out\/[^/]+\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.DataOutIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/data_out\/[^/]+\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.DataOutIntegrationsMigration,
  },
  // Warehouse sync — the whole `/integrations/warehouse` tree — dark behind
  // `warehouse-integration-migration` (FPS-2317). Same two-entry shape and reasoning as the two
  // above: the migrated URL drops `journeys`, and the `journeys` twin is claimed only so
  // `JourneysWarehouseHandoff` can forward to it (rendering the embedded host while the flag is
  // off), which keeps every Ember-side link working untouched.
  //
  // Every route *below* `/integrations/warehouse`: the three connector landing pages (FPS-2698),
  // the connected-config view keyed by storage provider (FPS-2717), and the create wizard at
  // `/:type/sync` (FPS-2730).
  //
  // The bare `/warehouse` path is deliberately excluded — it has no Hydra route, and Ember serves
  // it (the command palette links there). Claiming a URL the shell cannot render leaves ownership
  // saying `hydra` while Ember is on screen, which wedges active-route sync until a reload.
  //
  // Both patterns terminate for the same reason: only `<type>` and `<type>/sync` are real routes,
  // so anything deeper (`/warehouse/s3/sync/extra`) stays with the embedded app.

  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/warehouse\/[^/]+(?:\/sync)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WarehouseIntegrationMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/warehouse\/[^/]+(?:\/sync)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WarehouseIntegrationMigration,
  },
  // The Customer.io API connector page (FPS-2752), dark behind
  // `integration-connectors-migration`. Two entries because the migrated URL drops the `journeys`
  // segment: `/integrations/customerio` is where the page lives, and the `journeys` twin stays
  // claimed so `JourneysCustomerioHandoff` can forward the Ember-side links to it.
  //
  // Both patterns terminate at `customerio`: the page has no children, so anything deeper stays
  // with the embedded app. The sibling connector pages on the same flag (`mparticle_out`, the
  // generic `:type` page) get their own entries as they land.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/customerio\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.IntegrationConnectorsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/customerio\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.IntegrationConnectorsMigration,
  },
  // Design Studio dashboard (CON-7495), fully rolled out: the page is native
  // here and the embedded Design Studio bundle no longer carries a dashboard
  // route to fall back to. The pattern matches only the exact dashboard URL
  // (plus optional trailing slash, query, or hash): the native route is a
  // static path with no children, so `/dashboard/<sub>` and `/dashboard-*` URLs
  // route to the `/design-studio/$` splat and must stay with the embedded app —
  // ownership mirrors that.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/dashboard\/?(?:[#?]|$)/i },
  // Design Studio global styles (CON-7588), fully rolled out: the page is
  // native here and the embedded Design Studio bundle no longer carries a
  // global-styles route to fall back to (CON-7922). The pattern matches only
  // the exact global-styles URL (plus optional trailing slash, query, or hash):
  // the native route is a static path with no children, so `/global-styles/<sub>`
  // and `/global-styles-*` URLs route to the `/design-studio/$` splat and must
  // stay with the embedded app — ownership mirrors that.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/global-styles\/?(?:[#?]|$)/i },
  // Design Studio brand page (CON-7724). Ownership is dark behind the
  // `ds-brand-document` flag — the same flag `DesignStudioBrandRoute` gates the
  // rendered component on, so the ownership answer and the rendered app always
  // agree (the same-flag invariant the global-styles entry used while it was
  // flag-gated). Flag off, the URL falls back to the embedded Design Studio
  // bundle. The pattern matches only the exact brand URL (plus optional
  // trailing slash, query, or hash): the native route is a static path with no
  // children, so `/brand/<sub>` and `/brand-*` URLs route to the
  // `/design-studio/$` splat and must stay with the embedded app — ownership
  // mirrors that.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/brand\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.DsBrandDocument,
  },
];

/**
 * Route ownership is a function of the URL *and* the account's feature flags
 * (flag-gated migration entries), so the account is a required input. A null
 * account means "not loaded yet" — flags read their defaults, so flag-gated
 * Hydra routes classify as embedded until the real account is supplied.
 *
 * Call-time consumers (event handlers, the Ember `window.__hydra__` bridge)
 * pass `accountStore.get()`. Render-time consumers must NOT — a component
 * rendered before auth completes would cache the flag-default answer
 *; use `useRouteOwner`/`useIsHydraRoute` instead.
 *
 * Public-flag-gated entries (`/signup`) additionally depend on
 * `signupPublicFlagsStore` being resolved: while it is `null`, the entry reads
 * as `embedded`. For call-time consumers that is guaranteed pre-auth by the
 * eager prefetch in `utils/inject-globals.ts`.
 */
export function getRouteOwner(
  path: string,
  account: RouteOwnershipAccount,
  publicFlags: SignupPublicFlags = signupPublicFlagsStore.get(),
): RouteOwner {
  return (
    ROUTE_OWNERSHIP_PATTERNS.find(
      ({ pattern, flag, signupPublicFlag }) =>
        (flag == null || accountHasFeatureFlag(account, flag)) &&
        (signupPublicFlag == null || hasSignupPublicFlag(publicFlags, signupPublicFlag)) &&
        pattern.test(path),
    )?.owner ?? 'embedded'
  );
}

export function isHydraRoute(
  path: string,
  account: RouteOwnershipAccount,
  publicFlags: SignupPublicFlags = signupPublicFlagsStore.get(),
): boolean {
  return getRouteOwner(path, account, publicFlags) === 'hydra';
}

/**
 * Reactive ownership for render-time consumers: subscribes to the account and
 * the signup public flags so flag-gated entries re-evaluate when either loads.
 */
export function useRouteOwner(path: string): RouteOwner {
  const account = useStore(accountStore);
  const publicFlags = useStore(signupPublicFlagsStore);
  return getRouteOwner(path, account, publicFlags);
}

export function useIsHydraRoute(path: string): boolean {
  return useRouteOwner(path) === 'hydra';
}
